/**
 * The theme button: remembering what was chosen, and applying it.
 *
 * All the deciding lives in `domain/theme.ts`; this is the wiring — an attribute on the
 * root element, a value in `localStorage`, and a listener for the system changing its
 * mind under us.
 *
 * ## Why the attribute is only set for an explicit choice
 *
 * The stylesheet already answers `prefers-color-scheme` on its own, so a page whose
 * script has not run yet — or has failed — is still the right colour. Writing the
 * resolved theme onto the root element in every case would take that over, and the two
 * could then disagree; removing the attribute instead hands the decision back to CSS,
 * which is the one place that can make it before the first paint.
 */

import {
  describeThemeChoice,
  nextThemeChoice,
  parseThemeChoice,
  resolveTheme,
  type Theme,
  type ThemeChoice,
  THEME_STORAGE_KEY,
} from '../domain/theme.js';

const DARK_QUERY = '(prefers-color-scheme: dark)';

export interface ThemeToggleOptions {
  readonly button: HTMLButtonElement;
  /**
   * The theme in force, whenever it changes — including once during construction, so a
   * caller need not apply the starting theme itself.
   *
   * Only fires when the *theme* changes, not when the choice does: switching from
   * following a dark system to choosing dark outright looks like nothing happened, and
   * anything downstream would only be redoing work it had already done.
   */
  readonly onThemeChanged: (theme: Theme) => void;
}

export class ThemeToggle {
  private readonly button: HTMLButtonElement;
  private readonly onThemeChanged: (theme: Theme) => void;
  private readonly systemDark: MediaQueryList;

  private choice: ThemeChoice;
  private applied: Theme | undefined;

  constructor(options: ThemeToggleOptions) {
    this.button = options.button;
    this.onThemeChanged = options.onThemeChanged;
    this.systemDark = window.matchMedia(DARK_QUERY);
    this.choice = parseThemeChoice(read());

    this.button.addEventListener('click', () => {
      this.set(nextThemeChoice(this.choice));
    });

    // Someone flipping their whole machine to dark at sunset should be followed, not
    // waited on until the next reload — but only while this session is following it.
    this.systemDark.addEventListener('change', () => {
      if (this.choice === 'system') this.apply();
    });

    this.apply();
  }

  /** The theme in force right now. */
  get theme(): Theme {
    return resolveTheme(this.choice, this.systemDark.matches);
  }

  private set(choice: ThemeChoice): void {
    this.choice = choice;

    // A browser refusing to store — private browsing, or storage that is full — is not a
    // reason to leave the button unable to change the theme for this session.
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, choice);
    } catch (error) {
      console.warn('The theme choice could not be remembered.', error);
    }

    this.apply();
  }

  private apply(): void {
    const theme = this.theme;
    const root = document.documentElement;

    if (this.choice === 'system') {
      delete root.dataset.theme;
    } else {
      root.dataset.theme = this.choice;
    }

    const { text, description } = describeThemeChoice(this.choice, theme);
    this.button.textContent = text;
    this.button.title = description;
    this.button.setAttribute('aria-label', description);

    if (theme === this.applied) return;
    this.applied = theme;
    this.onThemeChanged(theme);
  }
}

function read(): string | null {
  try {
    return window.localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    // Same reasoning as writing: an unreadable store means no remembered choice, which
    // is a state this already handles.
    return null;
  }
}
